// Formatting helpers used across request views.

import type { Priority, RequestStatus, SlaStatus } from '@/lib/types'

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatRelative(iso: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  const diffMs = Date.now() - date.getTime()
  const seconds = Math.round(diffMs / 1000)
  const minutes = Math.round(seconds / 60)
  const hours = Math.round(minutes / 60)
  const days = Math.round(hours / 24)
  if (seconds < 60) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 14) return `${days}d ago`
  return formatDateTime(iso)
}

export function deadlineDelta(deadlineISO: string): string {
  const now = Date.now()
  const target = new Date(deadlineISO).getTime()
  const diffMs = target - now
  const minutes = Math.round(diffMs / 60000)
  if (Math.abs(minutes) < 60) return `${minutes >= 0 ? '' : '-'}${Math.abs(minutes)}m`
  const hours = Math.round(minutes / 60)
  if (Math.abs(hours) < 48) return `${hours >= 0 ? '' : '-'}${Math.abs(hours)}h`
  const days = Math.round(hours / 24)
  return `${days >= 0 ? '' : '-'}${Math.abs(days)}d`
}

export const STATUS_COLOR: Record<RequestStatus, string> = {
  pending: 'bg-slate-100 text-slate-700 ring-slate-200',
  in_review: 'bg-blue-100 text-blue-700 ring-blue-200',
  approved: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  denied: 'bg-rose-100 text-rose-700 ring-rose-200',
  completed: 'bg-slate-100 text-slate-700 ring-slate-200',
}

export const PRIORITY_COLOR: Record<Priority, string> = {
  urgent: 'bg-rose-100 text-rose-700 ring-rose-200',
  high: 'bg-amber-100 text-amber-700 ring-amber-200',
  normal: 'bg-blue-100 text-blue-700 ring-blue-200',
  low: 'bg-slate-100 text-slate-700 ring-slate-200',
}

export const SLA_COLOR: Record<SlaStatus, string> = {
  on_track: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  at_risk: 'bg-amber-100 text-amber-700 ring-amber-200',
  breached: 'bg-rose-100 text-rose-700 ring-rose-200',
  met: 'bg-slate-100 text-slate-700 ring-slate-200',
}

export function statusLabel(status: RequestStatus): string {
  return status.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export function slaLabel(status: SlaStatus): string {
  if (status === 'on_track') return 'On Track'
  if (status === 'at_risk') return 'At Risk'
  if (status === 'breached') return 'Breached'
  if (status === 'met') return 'Met'
  return status
}
